import type { Intent, Role, SubscribeRequest, SubscribeResponse } from '../../shared/contract';
import { HONEYPOT_FIELD } from '../../shared/contract';

/**
 * Same-origin endpoint backed by the Cloudflare Pages Function in `functions/`.
 * The provider credentials live in server environment variables, so nothing
 * in this bundle identifies the mailing list provider or the list itself.
 */
const SUBSCRIBE_ENDPOINT = '/api/subscribe';

export interface SubscribeParams {
  email: string;
  role: Role;
  /** Value of the honeypot input. Empty for real people. */
  honeypot: string;
  /** Defaults to a normal sign-up. */
  intent?: Intent;
}

/**
 * Posts a waitlist signup. Never throws for an expected failure - transport
 * errors are mapped onto the same `error` response the server returns, so
 * callers handle exactly one shape.
 */
export async function subscribe({
  email,
  role,
  honeypot,
  intent,
}: SubscribeParams): Promise<SubscribeResponse> {
  const payload: SubscribeRequest = {
    email,
    role,
    [HONEYPOT_FIELD]: honeypot,
    ...(intent ? { intent } : {}),
  };

  let response: Response;
  try {
    response = await fetch(SUBSCRIBE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    // Offline, DNS failure, or a content blocker that ate the request.
    return { status: 'error', message: 'Network unavailable. Check your connection and try again.' };
  }

  const body = (await response.json().catch(() => null)) as SubscribeResponse | null;
  if (!body) {
    return { status: 'error', message: 'Something went wrong. Please try again in a moment.' };
  }

  return body;
}
